import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { LibraryBig, Menu, Plus, Search, X } from "lucide-react";
import { type FormEvent, useId, useState } from "react";

import { Mark } from "@/components/brand/Mark";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { libraryQuery } from "@/lib/api/queries";
import { useAuthState } from "@/lib/auth/context";
import { cn } from "@/lib/cn";
import { useOpenUpload } from "@/lib/upload-route";

export function AppHeader() {
  const openUpload = useOpenUpload();
  const auth = useAuthState();
  const [menuOpen, setMenuOpen] = useState(false);
  const gated = auth.enabled && auth.status !== "ready";
  const UserButton = auth.enabled && auth.status === "ready" ? auth.UserButton : null;
  const OrganizationSwitcher =
    auth.enabled && auth.status === "ready" ? auth.OrganizationSwitcher : null;

  return (
    <>
      <header
        className="sticky top-0 z-(--z-header) flex h-16 items-center gap-2 border-b border-line bg-surface/95 px-4 backdrop-blur-md lg:fixed lg:inset-y-0 lg:left-0 lg:h-dvh lg:w-64 lg:flex-col lg:items-stretch lg:gap-5 lg:border-r lg:border-b-0 lg:bg-surface lg:p-5"
        data-testid="app-header"
      >
        <Link
          to="/"
          search={{}}
          className="flex shrink-0 items-center gap-2.5 rounded-control text-lg font-semibold tracking-tighter lg:order-1 lg:h-10"
          aria-label="instrukt, library"
        >
          <span className="flex size-9 items-center justify-center rounded-control bg-surface-2">
            <Mark size={25} />
          </span>
          <span className="leading-none">instrukt</span>
        </Link>

        {gated ? null : (
          <>
            <Button
              onClick={() => openUpload()}
              className="order-2 ml-auto shrink-0 max-lg:size-11 max-lg:p-0 lg:ml-0 lg:w-full"
              data-testid="add-video"
            >
              <Plus aria-hidden="true" />
              <span className="max-lg:sr-only">Add video</span>
            </Button>
            <div className="hidden lg:order-3 lg:block">
              <SearchField hideOnLibrary />
            </div>
            <nav className="hidden lg:order-4 lg:block" aria-label="Main navigation">
              <LibraryLink />
            </nav>
            <RecentMachines />
            <div className="order-3 flex items-center gap-1 lg:order-6 lg:mt-auto lg:flex-col lg:items-stretch">
              {OrganizationSwitcher ? (
                <div data-slot="organization-switcher" className="max-lg:hidden">
                  <OrganizationSwitcher />
                </div>
              ) : null}
              {UserButton ? (
                <div data-slot="user-button" className="flex shrink-0 items-center">
                  <UserButton />
                </div>
              ) : null}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="order-4 size-11 lg:hidden"
              aria-label="Search"
              onClick={() => setMenuOpen(true)}
            >
              <Search aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="order-5 size-11 lg:hidden"
              aria-label="Open navigation"
              onClick={() => setMenuOpen(true)}
            >
              <Menu aria-hidden="true" />
            </Button>
          </>
        )}
      </header>

      <Dialog open={menuOpen} onOpenChange={setMenuOpen}>
        <DialogContent
          className="inset-y-0 right-0 left-auto flex h-dvh max-h-none w-[min(22rem,calc(100vw-2rem))] rounded-none border-l p-5 sm:inset-y-0 sm:right-0 sm:left-auto sm:w-[min(22rem,calc(100vw-2rem))] sm:translate-x-0 sm:translate-y-0 sm:rounded-none sm:p-5"
          aria-describedby={undefined}
        >
          <DialogHeader>
            <DialogTitle>Navigate instrukt</DialogTitle>
            <DialogDescription className="sr-only">
              Search and open your machine library.
            </DialogDescription>
          </DialogHeader>
          <LibraryLink onNavigate={() => setMenuOpen(false)} />
          <SearchField onSubmit={() => setMenuOpen(false)} />
          <RecentMachines onNavigate={() => setMenuOpen(false)} />
          <Button
            onClick={() => {
              setMenuOpen(false);
              openUpload();
            }}
          >
            <Plus aria-hidden="true" /> Add video
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function LibraryLink({ onNavigate }: { onNavigate?: () => void }) {
  const active = useRouterState({ select: (state) => state.location.pathname === "/" });
  return (
    <Link
      to="/"
      search={{}}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex min-h-11 items-center gap-3 rounded-control px-3 text-base font-medium",
        active
          ? "bg-accent-soft text-accent-text-on-soft"
          : "text-ink-2 hover:bg-surface-2 hover:text-ink",
      )}
    >
      <LibraryBig className="size-5" aria-hidden="true" /> Library
    </Link>
  );
}

function RecentMachines({ onNavigate }: { onNavigate?: () => void }) {
  const library = useQuery({ ...libraryQuery(), enabled: false });
  const machines = library.data?.machines.slice(0, 5) ?? [];
  if (machines.length === 0) return null;
  return (
    <nav
      className={cn("min-h-0 flex-col gap-1", onNavigate ? "flex" : "hidden lg:order-5 lg:flex")}
      aria-label="Recent machines"
    >
      <p className="px-3 pt-3 pb-1 text-xs font-semibold tracking-[0.08em] text-muted uppercase">
        Recent machines
      </p>
      {machines.map((machine) => (
        <Link
          key={machine.key}
          to="/machines/$key"
          params={{ key: machine.key }}
          search={{}}
          onClick={onNavigate}
          className="flex min-h-10 items-center gap-2.5 rounded-control px-3 text-sm text-ink-2 hover:bg-surface-2 hover:text-ink"
        >
          <span
            className={cn(
              "size-1.5 shrink-0 rounded-pill",
              machine.flag === "processing"
                ? "bg-info-dot"
                : machine.flag === "needs_review"
                  ? "bg-warn-dot"
                  : machine.flag === "failed"
                    ? "bg-danger"
                    : "bg-accent",
            )}
            aria-hidden="true"
          />
          <span className="truncate">{machine.name}</span>
        </Link>
      ))}
    </nav>
  );
}

function SearchField({
  onSubmit,
  hideOnLibrary = false,
}: {
  onSubmit?: () => void;
  hideOnLibrary?: boolean;
}) {
  const navigate = useNavigate();
  const id = useId();
  const { onLibrary, urlQuery } = useRouterState({
    select: (state) => {
      const q = (state.location.search as { q?: string }).q;
      return {
        onLibrary: state.location.pathname === "/",
        urlQuery: typeof q === "string" ? q : "",
      };
    },
  });
  const [draft, setDraft] = useState({ value: urlQuery, source: urlQuery });
  const value = draft.source === urlQuery ? draft.value : urlQuery;
  if (hideOnLibrary && onLibrary) return null;

  const change = (next: string) => {
    setDraft({ value: next, source: urlQuery });
    if (onLibrary) {
      void navigate({
        to: "/",
        search: (prev) => ({ ...prev, q: next.trim() || undefined }),
        replace: true,
      });
    }
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const q = value.trim();
    void navigate({ to: "/", search: q ? { q } : {}, replace: false });
    onSubmit?.();
  };
  const clear = () => {
    setDraft({ value: "", source: urlQuery });
    if (onLibrary) {
      void navigate({ to: "/", search: (prev) => ({ ...prev, q: undefined }), replace: true });
    }
  };

  return (
    <form role="search" onSubmit={submit} className="w-full">
      <label htmlFor={id} className="sr-only">
        Search machines and procedures
      </label>
      <div className="relative w-full">
        <Search
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted"
          aria-hidden="true"
        />
        <Input
          id={id}
          type="search"
          name="q"
          placeholder="Search library"
          value={value}
          onChange={(event) => change(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape" && value) {
              event.preventDefault();
              clear();
            }
          }}
          className="bg-page pr-10 pl-9"
          autoComplete="off"
          enterKeyHint="search"
        />
        {value ? (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear search"
            className="absolute top-1/2 right-0 inline-flex size-11 -translate-y-1/2 items-center justify-center rounded-control text-muted hover:text-ink"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </form>
  );
}
